import React from "react";
import { Link } from "react-router-dom";
import Modal from "../components/common/Modal";
import GameTypePickerModal from "../components/modals/GameTypePickerModal";
import getServerBaseUrl from "../utils/serverBaseUrl";

function ConfigPickerModal({ open, title, options, selectedValue, onClose, onSelect }) {
  const [query, setQuery] = React.useState("");

  React.useEffect(() => {
    if (!open) return;
    setQuery("");
  }, [open]);

  const normalizedQuery = query.trim().toLowerCase();
  const filteredOptions = Array.isArray(options)
    ? options.filter((option) => {
        const label = String(option.label ?? option.value ?? "").toLowerCase();
        return !normalizedQuery || label.includes(normalizedQuery);
      })
    : [];

  return (
    <Modal show={open} onClose={onClose} title={title}>
      <div className="w-[min(90vw,40rem)] bg-white p-4 dark:bg-slate-900">
        {Array.isArray(options) && options.length > 8 && (
          <div className="mb-3">
            <input
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search configurations"
              className="w-full rounded-2xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-cyan-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
            />
          </div>
        )}

        <div className="flex max-h-[55vh] flex-col gap-2 overflow-auto pr-1">
          {filteredOptions.length === 0 ? (
            <div className="text-sm text-slate-500 dark:text-slate-400">No configurations found.</div>
          ) : (
            filteredOptions.map((option) => {
              const isSelected = selectedValue === option.value;
              return (
                <button
                  key={`${title}-${String(option.value)}`}
                  type="button"
                  className={`w-full rounded-2xl border px-4 py-3 text-left text-sm transition ${
                    isSelected
                      ? "border-cyan-500 bg-cyan-50 text-cyan-800 dark:border-cyan-400 dark:bg-cyan-950/40 dark:text-cyan-200"
                      : "border-slate-300 bg-white text-slate-900 hover:border-cyan-400 hover:bg-cyan-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:border-cyan-500 dark:hover:bg-slate-700"
                  }`}
                  onClick={() => {
                    onSelect(option.value);
                    onClose();
                  }}
                >
                  <div className="font-medium">{option.label}</div>
                  {isSelected && (
                    <div className="mt-1 text-xs font-semibold uppercase tracking-[0.16em] opacity-80">Selected</div>
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>
    </Modal>
  );
}

function formatConfigKey(key) {
  const labels = {
    numWords: "Words",
    timeLimitSeconds: "Time limit",
    category: "Category",
    difficulty: "Difficulty",
    wordCount: "Words",
    drawCount: "Draw count",
  };
  if (labels[key]) return labels[key];
  return String(key || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatConfigValue(key, value) {
  if (key === "timeLimitSeconds") {
    const seconds = Number(value);
    if (Number.isFinite(seconds) && seconds > 0) {
      if (seconds % 60 === 0) {
        const minutes = seconds / 60;
        return `${minutes} minute${minutes === 1 ? "" : "s"}`;
      }
      return `${seconds} seconds`;
    }
  }
  return String(value);
}

function formatConfigLabel(configSnapshot = {}) {
  const entries = Object.entries(configSnapshot || {}).sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey));
  if (entries.length === 0) return "Default configuration";
  return entries.map(([key, value]) => `${formatConfigKey(key)}: ${formatConfigValue(key, value)}`).join(" | ");
}

function compareEntries(left, right, sortDirection) {
  const leftScore = Number(left?.score_value ?? 0);
  const rightScore = Number(right?.score_value ?? 0);
  if (leftScore !== rightScore) {
    return sortDirection === "asc" ? leftScore - rightScore : rightScore - leftScore;
  }
  return String(left?.player_username_clean || left?.player_username || "").localeCompare(
    String(right?.player_username_clean || right?.player_username || ""),
    undefined,
    { sensitivity: "base" }
  );
}

export default function HighScores() {
  const serverBaseUrl = React.useMemo(() => getServerBaseUrl(), []);
  const [games, setGames] = React.useState([]);
  const [selectedGame, setSelectedGame] = React.useState("");
  const [showGameTypeModal, setShowGameTypeModal] = React.useState(false);
  const [boards, setBoards] = React.useState([]);
  const [loadingGames, setLoadingGames] = React.useState(true);
  const [loadingBoards, setLoadingBoards] = React.useState(false);
  const [error, setError] = React.useState("");
  const [selectedBoardByGroup, setSelectedBoardByGroup] = React.useState({});
  const [configPickerGroup, setConfigPickerGroup] = React.useState(null);

  const pickerGameTypes = React.useMemo(
    () => games.map((game) => ({
      ...game,
      gameClass: game.gameClass || game.gameType,
      minPlayers: Number(game.minPlayers) || 1,
      maxPlayers: Number(game.maxPlayers) || Number(game.minPlayers) || 1,
    })),
    [games]
  );

  React.useEffect(() => {
    let active = true;
    async function loadGames() {
      setLoadingGames(true);
      setError("");
      try {
        const response = await fetch(`${serverBaseUrl}/api/high-scores/games`);
        const data = await response.json();
        if (!response.ok || !data?.ok) {
          throw new Error(data?.error || "Failed to load high score games.");
        }
        if (!active) return;
        const availableGames = Array.isArray(data.games) ? data.games : [];
        setGames(availableGames);
        if (availableGames.length > 0) {
          const randomGame = availableGames[Math.floor(Math.random() * availableGames.length)];
          setSelectedGame((current) => current || randomGame.gameType);
        }
      } catch (err) {
        if (!active) return;
        setError(err.message || "Failed to load high score games.");
      } finally {
        if (active) setLoadingGames(false);
      }
    }
    loadGames();
    return () => { active = false; };
  }, [serverBaseUrl]);

  React.useEffect(() => {
    let active = true;
    async function loadBoards() {
      if (!selectedGame) {
        setBoards([]);
        return;
      }
      setLoadingBoards(true);
      setError("");
      try {
        const response = await fetch(`${serverBaseUrl}/api/high-scores/${encodeURIComponent(selectedGame)}`);
        const data = await response.json();
        if (!response.ok || !data?.ok) {
          throw new Error(data?.error || "Failed to load high score entries.");
        }
        if (!active) return;
        setBoards(Array.isArray(data.leaderboards) ? data.leaderboards : []);
      } catch (err) {
        if (!active) return;
        setError(err.message || "Failed to load high score entries.");
      } finally {
        if (active) setLoadingBoards(false);
      }
    }
    loadBoards();
    return () => { active = false; };
  }, [selectedGame, serverBaseUrl]);

  const selectedGameMeta = games.find((game) => game.gameType === selectedGame) || null;
  const boardGroups = React.useMemo(() => {
    const groups = new Map();

    for (const board of boards) {
      const groupKey = board.metricKey || board.leaderboardKey || board.bucketKey;
      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          key: groupKey,
          metricLabel: board.metricLabel || "Leaderboard",
          boards: [],
        });
      }
      groups.get(groupKey).boards.push(board);
    }

    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        boards: [...group.boards].sort((left, right) => formatConfigLabel(left.configSnapshot).localeCompare(formatConfigLabel(right.configSnapshot))),
      }))
      .sort((left, right) => left.metricLabel.localeCompare(right.metricLabel));
  }, [boards]);

  React.useEffect(() => {
    setSelectedBoardByGroup((current) => {
      const next = {};
      for (const group of boardGroups) {
        const currentBucketKey = current[group.key];
        const stillExists = group.boards.some((board) => board.bucketKey === currentBucketKey);
        next[group.key] = stillExists ? currentBucketKey : (group.boards[0]?.bucketKey || "");
      }
      return next;
    });
  }, [boardGroups]);

  return (
    <div className="w-full px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-5xl">
        <nav aria-label="Breadcrumb" className="mb-4">
          <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
            <Link to="/" className="font-medium transition-colors hover:text-slate-900 dark:hover:text-slate-100">
              Lobby
            </Link>
            <span aria-hidden="true">/</span>
            <span className="font-medium text-slate-700 dark:text-slate-200">High Scores</span>
          </div>
        </nav>

        <section className="overflow-hidden rounded-3xl border border-slate-200 bg-[linear-gradient(135deg,#f8fafc_0%,#ffffff_38%,#ecfeff_100%)] p-6 shadow-sm dark:border-slate-700 dark:bg-[linear-gradient(135deg,#111827_0%,#0f172a_42%,#082f49_100%)] sm:p-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-cyan-700 dark:text-cyan-300">
                Leaderboards
              </p>
              <p className="mt-4 text-sm leading-7 text-slate-600 dark:text-slate-300 sm:text-base">
                Browse leaderboards for games that support high scores. Rankings are grouped by game setup so every board stays comparable.
              </p>
            </div>

            <div className="w-full max-w-sm">
              <label htmlFor="high-score-game" className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-200">
                Choose a game
              </label>
              <button
                id="high-score-game"
                type="button"
                className="flex w-full items-center justify-between rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition hover:border-cyan-500 focus:border-cyan-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                onClick={() => setShowGameTypeModal(true)}
                disabled={loadingGames || games.length === 0}
              >
                <span>{selectedGameMeta?.displayName || "Select Game Type"}</span>
                <span className="text-xs text-slate-500 dark:text-slate-400">Change</span>
              </button>
            </div>
          </div>
        </section>

        {showGameTypeModal && (
          <GameTypePickerModal
            showGameTypePicker={showGameTypeModal}
            closeGameTypePicker={() => setShowGameTypeModal(false)}
            onSelect={(gameType) => {
              setSelectedGame(gameType);
              setShowGameTypeModal(false);
            }}
            selectedKey={selectedGame}
            mode="grid"
            gameTypes={pickerGameTypes}
            title="Select Game Type"
          />
        )}

        {configPickerGroup && (
          <ConfigPickerModal
            open={Boolean(configPickerGroup)}
            title={`Select ${configPickerGroup.metricLabel || "Configuration"}`}
            options={configPickerGroup.boards.map((board) => ({
              value: board.bucketKey,
              label: formatConfigLabel(board.configSnapshot),
            }))}
            selectedValue={selectedBoardByGroup[configPickerGroup.key] || configPickerGroup.boards[0]?.bucketKey || ""}
            onClose={() => setConfigPickerGroup(null)}
            onSelect={(nextBucketKey) => {
              setSelectedBoardByGroup((current) => ({
                ...current,
                [configPickerGroup.key]: nextBucketKey,
              }));
            }}
          />
        )}

        {error && (
          <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
            {error}
          </div>
        )}

        {selectedGameMeta && (
          <section className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900 sm:p-8">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-4">
                {selectedGameMeta.thumbnail && (
                  <img
                    src={selectedGameMeta.thumbnail}
                    alt={selectedGameMeta.displayName}
                    className="h-20 w-20 rounded-2xl bg-white object-contain shadow-sm dark:bg-slate-800"
                  />
                )}
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{selectedGameMeta.displayName}</h2>
                  <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                    {selectedGameMeta.description}
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-stretch gap-3 sm:items-end">
                <Link
                  to="/"
                  state={{ openCreateRoom: true, createGameType: selectedGameMeta.gameClass || selectedGameMeta.gameType }}
                  className="inline-flex items-center justify-center rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700"
                  onClick={() => { window.scrollTo({ top: 0, behavior: 'auto' }); }}
                >
                  Play Now
                </Link>
              </div>
            </div>
          </section>
        )}

        <div className="mt-6 grid gap-6">
          {loadingBoards && (
            <div className="rounded-3xl border border-slate-200 bg-white p-6 text-sm text-slate-600 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
              Loading high scores...
            </div>
          )}

          {!loadingBoards && selectedGame && boards.length === 0 && !error && (
            <div className="rounded-3xl border border-slate-200 bg-white p-6 text-sm text-slate-600 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
              No qualifying high scores have been recorded for this game yet.
            </div>
          )}

          {boardGroups.map((group) => {
            const activeBoard = group.boards.find((board) => board.bucketKey === selectedBoardByGroup[group.key]) || group.boards[0] || null;
            if (!activeBoard) return null;

            const sortDirection = activeBoard.entries?.[0]?.sort_direction === "asc" ? "asc" : "desc";
            const sortedEntries = [...(activeBoard.entries || [])].sort((left, right) => compareEntries(left, right, sortDirection));
            return (
              <section key={group.key} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900 sm:p-8">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100">{group.metricLabel || "Leaderboard"}</h3>
                    {group.boards.length > 1 ? (
                      <div className="mt-3">
                        <label htmlFor={`board-config-${group.key}`} className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                          Configuration
                        </label>
                        <button
                          id={`board-config-${group.key}`}
                          type="button"
                          className="flex w-full items-center justify-between gap-3 rounded-2xl border border-slate-300 bg-white px-4 py-3 text-left text-sm text-slate-900 shadow-sm outline-none transition hover:border-cyan-500 focus:border-cyan-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                          onClick={() => setConfigPickerGroup(group)}
                        >
                          <span className="truncate">{formatConfigLabel(activeBoard.configSnapshot)}</span>
                          <span className="shrink-0 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                            Change
                          </span>
                        </button>
                      </div>
                    ) : (
                      <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{formatConfigLabel(activeBoard.configSnapshot)}</p>
                    )}
                  </div>
                  <div className="rounded-2xl bg-cyan-50 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-cyan-700 dark:bg-cyan-950/40 dark:text-cyan-300">
                    {sortDirection === "asc" ? "Lower is better" : "Higher is better"}
                  </div>
                </div>

                <div className="mt-6 overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200 text-left dark:divide-slate-700">
                    <thead>
                      <tr className="text-xs uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                        <th className="px-3 py-3 font-semibold">Rank</th>
                        <th className="px-3 py-3 font-semibold">Player</th>
                        <th className="px-3 py-3 font-semibold">Score</th>
                        <th className="px-3 py-3 font-semibold">Achieved</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {sortedEntries.map((entry, index) => (
                        <tr key={entry.id || `${activeBoard.bucketKey}-${index}`} className="text-sm text-slate-700 dark:text-slate-200">
                          <td className="px-3 py-3 font-semibold">#{index + 1}</td>
                          <td className="px-3 py-3">{entry.player_username_clean || entry.player_username}</td>
                          <td className="px-3 py-3">{entry.score_display || entry.score_value}</td>
                          <td className="px-3 py-3">{entry.achieved_at ? new Date(entry.achieved_at).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}